import { Controller } from '@hotwired/stimulus';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip } from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip);

const ACCENT = '#6366F0';
const EVENT_TEXT = '#7a5b00';
const PERIOD_TEXT = '#047857';
const AXIS_TEXT = '#627291';

// Every mark's color, dash rhythm and width comes from the server
// (GoalsHelper::GOAL_CHART_MARKS), which the chart's key reads too, so a line
// and the swatch that explains it can never disagree. These are the fallbacks
// for a config rendered before the marks shipped.
// How far in from the left edge the first session sits when the chart draws a
// start-of-care anchor, as a share of the plot width.
const ANCHOR_GAP = 0.07;

const FALLBACK_MARKS = {
  data: { color: ACCENT, dash: [], width: 2 },
  trend: { color: 'rgba(99, 102, 240, 0.22)', dash: [], width: 8 },
  baseline: { color: '#9CA3AF', dash: [2, 3], width: 1.5 },
  soc: { color: '#475569', dash: [8, 3, 2, 3], width: 1.5 },
  soc_lead: { color: '#475569', dash: [4, 4], width: 2 },
  target: { color: '#10B981', dash: [12, 5], width: 2 },
  revision: { color: '#b98a00', dash: [4, 4], width: 1.5 },
  range_low: { color: 'rgba(99, 102, 240, 0.55)', dash: [3, 3], width: 1.5 },
  period: { color: 'rgba(16, 185, 129, 0.12)', edge: 'rgba(16, 185, 129, 0.45)', dash: [], width: 1 },
};

// Renders the Patient::Goals v3 "Goal progress" line chart in the goal's OWN
// framework units. The server (GoalsHelper#patient_goal_v3_chart_config)
// hands over one `config` JSON value:
//
//   {
//     kind:     'percent' | 'duration' | 'number' | 'select' | 'modifier' | 'range' | 'binary',
//     points:   [{ date: '6/12', y: 45, y2: null, tip: '9/20 (45%)', known: true }, ...],
//     baseline: 40 | null,          // current revision, flat fallback
//     target:   80 | null,
//     refs:     [{ baseline, target, target_label } | null, ...],  // per point, from its PINNED revision
//     events:   [{ index: 5, label: 'Target 60% → 80%' }],        // regrades, at the first point after they applied
//     period:   { from: 3, to: 5, label: 'This period' } | null,  // the sessions a progress report covers
//     axis:     { kind, min, max, step, label, ticks: { '1': 'Pass' }, unit }
//   }
//
// Baseline and target draw as dashed datasets. The target steps at revision
// boundaries when per-point refs are known (a regrade moves the bar
// mid-chart); points with no honest pin (migrated data) draw hollow and
// never step the line. Each regrade gets an amber dashed upright with its
// label, so a jump in the line reads as a decision, never as noise. A period
// shades the stretch of sessions it names, behind everything else.
//
// The chart is built lazily the first time its canvas becomes visible: the
// panel starts inside a closed Bootstrap collapse (display: none), and a
// page-wide "Toggle all" opens dozens at once. A config written onto the
// element after that (a progress report's graph options) redraws it in place.
export default class extends Controller {
  static targets = ['canvas'];
  static values = { config: Object };

  connect() {
    const points = (this.configValue || {}).points || [];
    if (!points.length) return;

    if (this.isVisible() || typeof IntersectionObserver === 'undefined') {
      this.render();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.observer = null;
      this.render();
    });
    this.observer.observe(this.element);
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    if (this.chart) this.chart.destroy();
    this.chart = null;
  }

  // Stimulus reports the initial value here as well, before connect() has
  // decided whether to draw; only a chart already on screen is redrawn. One
  // that is still waiting on the observer reads the new config when its turn
  // comes.
  configValueChanged() {
    if (!this.chart) return;
    this.chart.destroy();
    this.chart = null;
    this.render();
  }

  isVisible() {
    return this.element.offsetParent !== null;
  }

  render() {
    if (this.chart) return;
    const cfg = this.configValue || {};
    const points = cfg.points || [];
    if (!points.length) return;

    const axis = cfg.axis || {};
    const kind = cfg.kind || axis.kind || 'number';
    const labels = points.map((p) => p.date);
    const values = points.map((p) => p.y);
    const refs = cfg.refs || points.map(() => null);
    const events = cfg.events || [];
    const datasets = [];

    // Trend halo behind the data line. The server decides whether it is drawn,
    // so the key never advertises a band that is not on screen. Every point
    // here is a session: the start-of-care anchor is drawn in the margin and
    // never enters a series, so there is nothing to withhold from the average.
    const trend =
      cfg.trend != null ? cfg.trend : points.length >= 3 && !['binary', 'select', 'modifier'].includes(kind);
    if (trend) {
      datasets.push({
        gv3Role: 'halo',
        label: 'Trend',
        data: this.rollingAverage(values, 3),
        borderColor: this.mark(cfg, 'trend').color,
        backgroundColor: this.mark(cfg, 'trend').color,
        borderWidth: this.mark(cfg, 'trend').width,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      });
    }

    if (cfg.baseline != null) {
      datasets.push({
        gv3Role: 'baseline',
        label: 'Baseline',
        gvExtendLeft: true,
        data: labels.map(() => cfg.baseline),
        borderColor: this.mark(cfg, 'baseline').color,
        borderWidth: this.mark(cfg, 'baseline').width,
        borderDash: this.mark(cfg, 'baseline').dash,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      });
    }

    // Start of care spans the chart flat: it is an episode-level fact, not
    // revision state, so it must not ride the revision walk the way the target
    // does. Stepping it would start the line partway across on a goal whose
    // target moved, which reads as "care started in July".
    // Nothing to draw when the baseline already sits on those pixels: the key
    // names that one line for both.
    if (cfg.soc != null && !cfg.soc_matches_baseline) {
      datasets.push({
        gv3Role: 'soc',
        label: 'Start of care',
        gvExtendLeft: true,
        data: labels.map(() => cfg.soc),
        borderColor: this.mark(cfg, 'soc').color,
        borderWidth: this.mark(cfg, 'soc').width,
        borderDash: this.mark(cfg, 'soc').dash,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      });
    }

    // Target: stepped per point from the pinned revisions when any are
    // known (null where the pin is unknown, so the line honestly breaks),
    // flat from the current revision otherwise.
    const stepped = refs.map((ref) => (ref && ref.target != null ? ref.target : null));
    const anyStepped = stepped.some((t) => t != null);
    if (anyStepped || cfg.target != null) {
      datasets.push({
        gv3Role: 'target',
        label: 'Target',
        gvExtendLeft: true,
        data: anyStepped ? stepped : labels.map(() => cfg.target),
        stepped: 'before',
        borderColor: this.mark(cfg, 'target').color,
        borderWidth: this.mark(cfg, 'target').width,
        borderDash: this.mark(cfg, 'target').dash,
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: false,
        fill: false,
      });
    }

    // Range frameworks draw the low bound as a lighter second series.
    if (kind === 'range' && points.some((p) => p.y2 != null)) {
      datasets.push({
        gv3Role: 'range-low',
        label: 'Low bound',
        data: points.map((p) => (p.y2 == null ? null : p.y2)),
        borderColor: this.mark(cfg, 'range_low').color,
        backgroundColor: this.mark(cfg, 'range_low').color,
        borderWidth: this.mark(cfg, 'range_low').width,
        borderDash: this.mark(cfg, 'range_low').dash,
        tension: 0,
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: false,
      });
    }

    datasets.push({
      gv3Role: 'data',
      label: 'Session data',
      // The start-of-care leg reaches for THIS line, whichever dataset it is.
      gvAnchorTarget: true,
      data: values,
      showLine: kind !== 'binary',
      borderColor: this.mark(cfg, 'data').color,
      backgroundColor: this.mark(cfg, 'data').color,
      borderWidth: this.mark(cfg, 'data').width,
      tension: 0,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBorderColor: ACCENT,
      pointBorderWidth: 2,
      // Hollow point = revision unknown (migrated data), never a guess.
      pointBackgroundColor: points.map((p) => (p.known === false ? '#ffffff' : ACCENT)),
      fill: false,
    });

    // Whether the chart makes room for, and draws, the start-of-care anchor.
    const anchored = !!(cfg.lead_in && cfg.soc != null);
    const tickStep = this.tickStep(kind, axis);

    this.chart = new Chart(this.canvasTarget, {
      type: 'line',
      data: { labels, datasets },
      plugins: [
        this.periodBandPlugin(this.mark(cfg, 'period')),
        this.referenceStubPlugin(),
        this.eventsPlugin(this.mark(cfg, 'revision')),
        this.socAnchorPlugin(this.mark(cfg, 'soc_lead')),
      ],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        gv3Events: events,
        // The sessions a progress report covers, shaded behind the data (see periodBandPlugin).
        gv3Period: cfg.period || null,
        // Where the patient started, drawn in the left margin rather than
        // seated in a series (see socAnchorPlugin).
        gv3SocAnchor: anchored ? { y: cfg.soc } : null,
        layout: { padding: { top: 16, right: 16, bottom: 4, left: 4 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            filter: (item) => item.dataset.gv3Role === 'data' || item.dataset.gv3Role === 'range-low',
            callbacks: {
              label: (item) => {
                const point = points[item.dataIndex] || {};
                if (item.dataset.gv3Role === 'range-low')
                  return `Low bound: ${this.formatTick(kind, axis, item.parsed.y)}`;
                return point.tip || this.formatTick(kind, axis, item.parsed.y);
              },
              afterLabel: (item) => {
                const point = points[item.dataIndex] || {};
                const lines = [];
                const ref = refs[item.dataIndex];
                if (ref && ref.target_label) lines.push(`Target then: ${ref.target_label}`);
                if (point.known === false) lines.push('Revision unknown (migrated data)');
                return lines.join('\n');
              },
            },
          },
        },
        scales: {
          y: {
            min: axis.min,
            max: axis.max,
            title: axis.label
              ? { display: true, text: axis.label, color: AXIS_TEXT, font: { size: 11 } }
              : { display: false },
            ticks: {
              stepSize: tickStep,
              autoSkip: !this.forceAllTicks(kind),
              callback: (value) => this.formatTick(kind, axis, value),
              color: AXIS_TEXT,
              font: { size: 11 },
            },
            afterBuildTicks: (scale) => {
              const explicit = this.explicitTicks(kind, axis);
              if (explicit) scale.ticks = explicit;
            },
            grid: { display: false },
          },
          x: {
            // A line chart on a category axis puts the first point hard against
            // the plot's left edge, leaving no margin for the start-of-care
            // anchor. Chart.js only offers a symmetric offset, which insets by
            // half a slot at BOTH ends: too wide a gap on a goal with few
            // sessions, and dead space on the right where nothing needs it. So
            // widen the value range on the left only, by a fixed share of the
            // plot rather than a share of a slot, which keeps the gap the same
            // size whatever the session count and leaves the last point flush
            // against the right edge.
            offset: false,
            afterBuildTicks: (scale) => {
              if (!anchored) return;
              // Chart.js exposes the category range only as these two, which
              // getPixelForValue reads directly; there is no public equivalent.
              /* eslint-disable no-underscore-dangle */
              const inset = this.anchorInset(scale._valueRange);
              scale._startValue -= inset;
              scale._valueRange += inset;
              /* eslint-enable no-underscore-dangle */
            },
            grid: { display: false },
            ticks: { color: '#9CA3AF', font: { size: 11 } },
          },
        },
      },
    });
  }

  // Fractional axis bounds need sub-integer tick steps so the labeled values
  // (0/1 for binary, whole levels for scales) land on real ticks. Durations
  // widen their minute step so a long goal never crams a label per minute
  // into a 200px canvas.
  tickStep(kind, axis) {
    if (kind === 'binary') return 0.25;
    if (kind === 'select' || kind === 'modifier') return 0.5;
    if (kind === 'duration') {
      const span = (axis.max || 0) - (axis.min || 0);
      return 60 * Math.max(1, Math.ceil(span / 60 / 6));
    }
    return axis.step == null ? undefined : axis.step;
  }

  // Labeled scales draw every tick (the labels ARE the scale); continuous
  // axes let Chart.js thin them.
  forceAllTicks(kind) {
    return kind === 'binary' || kind === 'select' || kind === 'modifier';
  }

  // A select axis places its ticks exactly where the server labeled them,
  // so a scale whose rungs are not on the step grid (the MMT grades chart at
  // their own numbers) still labels every rung it names, and nothing draws
  // blank half-step ticks between named levels. Returns null for every
  // other kind, which keeps Chart.js's generated ticks.
  explicitTicks(kind, axis) {
    if (kind !== 'select') return null;
    const values = Object.keys((axis && axis.ticks) || {})
      .map(Number)
      .filter((v) => Number.isFinite(v))
      .sort((a, b) => a - b);
    return values.length ? values.map((value) => ({ value })) : null;
  }

  // Axis tick labels in framework units. Labeled scales (binary, named
  // selects, Ordinal endpoints) only label the ticks the server named.
  formatTick(kind, axis, value) {
    const ticks = axis.ticks || {};
    const key = String(value);
    if (kind === 'binary' || kind === 'select') return ticks[key] || '';
    if (kind === 'modifier') return Number.isInteger(value) ? String(value) : '';
    if (kind === 'percent') return `${value}%`;
    if (kind === 'duration') return this.mmss(value);
    if (ticks[key]) return `${value} ${ticks[key]}`;
    return `${value}${axis.unit ? ` ${axis.unit}` : ''}`;
  }

  mmss(totalSeconds) {
    if (totalSeconds == null || Number.isNaN(totalSeconds)) return '';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  // The reporting period a progress report covers, shaded behind the data: a
  // pale band from the slot before its first session to the slot after its
  // last, an upright at each end, and the period's name at the top when the
  // band is wide enough to carry it. It lives on the category axis, so it
  // marks the SESSIONS in the period: the space between two sessions is not
  // calendar time, and the edges are not dates. Drawn before everything else
  // so nothing on the chart is tinted by it.
  periodBandPlugin(mark) {
    return {
      id: 'gv3PeriodBand',
      beforeDatasetsDraw: (chart) => {
        // From config.options, like the events plugin: Chart.js does not carry
        // custom keys onto the normalized chart.options.
        const period = chart.config.options && chart.config.options.gv3Period;
        if (!period || period.from == null || period.to == null) return;
        const { ctx, chartArea, scales } = chart;
        if (!scales.x) return;

        const count = ((chart.data && chart.data.labels) || []).length;
        const [left, right] = this.periodEdges(period, count, chartArea, (i) => scales.x.getPixelForValue(i));
        if (right - left < 1) return;

        ctx.save();
        ctx.fillStyle = mark.color;
        ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
        ctx.strokeStyle = mark.edge || mark.color;
        ctx.lineWidth = mark.width || 1;
        ctx.setLineDash(mark.dash || []);
        [left, right].forEach((x) => {
          ctx.beginPath();
          ctx.moveTo(x, chartArea.top);
          ctx.lineTo(x, chartArea.bottom);
          ctx.stroke();
        });
        if (period.label) {
          ctx.setLineDash([]);
          ctx.font = '10px sans-serif';
          const fits = typeof ctx.measureText !== 'function' || ctx.measureText(period.label).width + 8 <= right - left;
          if (fits) {
            ctx.fillStyle = PERIOD_TEXT;
            ctx.textAlign = 'left';
            ctx.fillText(period.label, left + 4, chartArea.top + 9);
          }
        }
        ctx.restore();
      },
    };
  }

  // Where the band starts and ends: halfway to the neighbouring session on
  // either side, or the plot edge when the period reaches the first or last
  // session on the chart. Indices past either end are pulled back onto it.
  periodEdges(period, count, chartArea, xFor) {
    if (count < 1) return [chartArea.left, chartArea.left];
    const from = Math.min(Math.max(0, period.from), count - 1);
    const to = Math.min(Math.max(from, period.to), count - 1);
    const left = from === 0 ? chartArea.left : (xFor(from - 1) + xFor(from)) / 2;
    const right = to >= count - 1 ? chartArea.right : (xFor(to) + xFor(to + 1)) / 2;
    return [Math.max(chartArea.left, left), Math.min(chartArea.right, right)];
  }

  // Centered moving average, window 3; the ends taper naturally.
  // A reference line answers "where does this goal stand" for the whole
  // episode, so it has to reach the whole plot. Its dataset starts at the first
  // session, which the anchor gap pushes in from the left edge, leaving that
  // strip bare. This carries each tagged line from the edge to its first point
  // at that point's value, in the line's own color, rhythm and weight.
  //
  // Drawn before the datasets so the data and the anchor sit on top of it, and
  // a no-op when there is no gap, which is every chart without an anchor.
  referenceStubPlugin() {
    return {
      id: 'gv3RefStub',
      beforeDatasetsDraw: (chart) => {
        const { ctx, chartArea } = chart;
        chart.getSortedVisibleDatasetMetas().forEach((meta) => {
          const dataset = meta.controller.getDataset();
          if (!dataset.gvExtendLeft) return;

          const first = meta.data.find((el) => el && !el.skip && Number.isFinite(el.x) && Number.isFinite(el.y));
          if (!first || first.x - chartArea.left < 1) return;

          ctx.save();
          ctx.strokeStyle = dataset.borderColor;
          ctx.lineWidth = dataset.borderWidth;
          ctx.setLineDash(dataset.borderDash || []);
          ctx.beginPath();
          ctx.moveTo(chartArea.left, first.y);
          ctx.lineTo(first.x, first.y);
          ctx.stroke();
          ctx.restore();
        });
      },
    };
  }

  // How much to widen the category range on the LEFT so the first session sits
  // ANCHOR_GAP of the way into the plot, leaving the last one flush against the
  // right edge. Measured against the plot rather than a slot, so the gap is the
  // same size on a goal with three sessions as on one with thirty.
  anchorInset(valueRange) {
    return (valueRange * ANCHOR_GAP) / (1 - ANCHOR_GAP);
  }

  // The start-of-care anchor: a hollow diamond just inside the plot's left
  // margin with a dashed leg into the first session, marking the stretch
  // between where the patient started and the first thing anyone recorded.
  //
  // It is drawn rather than plotted on purpose. As an axis category it would
  // occupy a full slot on a category scale, so on a goal with a few sessions
  // the interval where nothing happened would take the loudest share of the
  // chart. Drawn here it stays a fixed sliver however sparse the data, the axis
  // keeps showing only dates something was recorded, and no series carries a
  // value that never happened, which is why nothing needs an index offset.
  socAnchorPlugin(mark) {
    return {
      id: 'gv3SocAnchor',
      afterDatasetsDraw: (chart) => {
        // From config.options, like the events plugin beside it: Chart.js
        // normalizes chart.options and does not carry custom keys onto it.
        const anchor = chart.config.options && chart.config.options.gv3SocAnchor;
        if (!anchor || anchor.y == null) return;

        const meta = chart.getSortedVisibleDatasetMetas().find((m) => m.controller.getDataset().gvAnchorTarget);
        if (!meta) return;

        // The first PLOTTED point, not index 0: a series can legitimately open
        // with nulls, and the leg must reach the first thing actually drawn.
        const first = meta.data.find((el) => el && !el.skip && Number.isFinite(el.x) && Number.isFinite(el.y));
        if (!first) return;

        const { ctx, chartArea, scales } = chart;
        const y = scales.y.getPixelForValue(anchor.y);
        const x = chartArea.left + 6;
        // No room for a leg is better than a diamond overlapping the first point.
        if (first.x - x < 10) return;

        ctx.save();
        ctx.strokeStyle = mark.color;
        ctx.lineWidth = mark.width;
        ctx.setLineDash(mark.dash || []);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(first.x, first.y);
        ctx.stroke();

        ctx.setLineDash([]);
        ctx.lineWidth = 2;
        ctx.fillStyle = '#ffffff';
        const r = 4.5;
        ctx.beginPath();
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r, y);
        ctx.lineTo(x, y + r);
        ctx.lineTo(x - r, y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      },
    };
  }

  // A mark from the server's vocabulary, falling back to the built-in copy.
  mark(cfg, key) {
    const marks = cfg.marks || {};
    return { ...FALLBACK_MARKS[key], ...(marks[key] || {}) };
  }

  rollingAverage(values, window) {
    const half = Math.floor(window / 2);
    return values.map((_, i) => {
      const lo = Math.max(0, i - half);
      const hi = Math.min(values.length - 1, i + half);
      let sum = 0;
      let count = 0;
      for (let j = lo; j <= hi; j += 1) {
        if (typeof values[j] === 'number') {
          sum += values[j];
          count += 1;
        }
      }
      return count > 0 ? sum / count : null;
    });
  }

  // Draws each regrade as an amber dashed upright between the session it
  // applied before and the first session after, labeled with the change.
  eventsPlugin(revisionMark) {
    return {
      id: 'gv3Events',
      afterDraw(chart) {
        const evs = (chart.config.options && chart.config.options.gv3Events) || [];
        if (!evs.length) return;
        const { ctx, chartArea, scales } = chart;
        if (!scales.x) return;
        ctx.save();
        evs.forEach((ev) => {
          const at = scales.x.getPixelForValue(ev.index);
          const before = ev.index > 0 ? scales.x.getPixelForValue(ev.index - 1) : at;
          const x = (at + before) / 2;
          ctx.strokeStyle = revisionMark.color;
          ctx.setLineDash(revisionMark.dash);
          ctx.lineWidth = revisionMark.width;
          ctx.beginPath();
          ctx.moveTo(x, chartArea.top);
          ctx.lineTo(x, chartArea.bottom);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = EVENT_TEXT;
          ctx.font = '10px sans-serif';
          ctx.textAlign = x > (chartArea.left + chartArea.right) / 2 ? 'right' : 'left';
          ctx.fillText(ev.label, x + (ctx.textAlign === 'left' ? 4 : -4), chartArea.top + 9);
        });
        ctx.restore();
      },
    };
  }
}
